// DateSelector.js
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatShortDate, toDateInputValue, fromDateInputValue } from '../utils';
import sprite from '../images/icons.svg';

const DateSelector = ({ selectedDate, setSelectedDate, type }) => {
  const dateInputRef = useRef(null);
  const { t, i18n } = useTranslation();

  const isDesktop = type === 'desktop';
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);

  const currentSelected = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
  const selectedMidnight = new Date(currentSelected);
  selectedMidnight.setHours(0, 0, 0, 0);

  const maxFutureDate = new Date(todayMidnight);
  maxFutureDate.setDate(todayMidnight.getDate() + 7);

  const canChangeBack = isDesktop || selectedMidnight > todayMidnight;
  const canChangeForward = isDesktop || selectedMidnight < maxFutureDate;
  const isToday = selectedMidnight.getTime() === todayMidnight.getTime();

  const handlePreviousDay = () => {
    if (!canChangeBack) return;
    const previousDay = new Date(currentSelected);
    previousDay.setDate(currentSelected.getDate() - 1);
    setSelectedDate(previousDay);
  };

  const handleNextDay = () => {
    if (!canChangeForward) return;
    const nextDay = new Date(currentSelected);
    nextDay.setDate(currentSelected.getDate() + 1);
    setSelectedDate(nextDay);
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    const newDate = fromDateInputValue(value);
    if (newDate && !isNaN(newDate.getTime())) {
      setSelectedDate(newDate);
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handleOpenPicker = () => {
    if (dateInputRef.current) {
      if (typeof dateInputRef.current.showPicker === 'function') {
        try {
          dateInputRef.current.showPicker();
        } catch (err) {
          dateInputRef.current.focus();
        }
      } else {
        dateInputRef.current.focus();
      }
    }
  };

  return (
    <span className="select-date flex flex-align-center-h wrapper-width center">
      {canChangeBack ? (
        <button
          type="button"
          className="date-nav-arrow-btn"
          onClick={handlePreviousDay}
          aria-label="Previous day"
        >
          <svg style={{ transform: 'rotate(180deg)', cursor: 'pointer' }} width="31" height="27">
            <use href={sprite + "#svg-arrow-right"} />
          </svg>
        </button>
      ) : (
        <span className="date-nav-arrow-disabled">
          <svg width="31" height="27">
            <use href={sprite + "#svg-arrow-left"} />
          </svg>
        </span>
      )}

      <div className="date-display-wrapper flex flex-align-center-h">
        <button
          type="button"
          className="date-picker-trigger-btn flex flex-align-center-h"
          onClick={handleOpenPicker}
          title={t('jump-to-date')}
          aria-label={t('jump-to-date')}
        >
          <span className="date-text bold">{formatShortDate(currentSelected, null, i18n.language)}</span>
          <svg className="calendar-icon" width="20" height="20" aria-hidden="true">
            <use href={sprite + "#svg-calendar"} />
          </svg>
        </button>

        <input
          ref={dateInputRef}
          type="date"
          className="date-picker-input"
          value={toDateInputValue(currentSelected)}
          onChange={handleDateChange}
          min={!isDesktop ? toDateInputValue(todayMidnight) : undefined}
          max={!isDesktop ? toDateInputValue(maxFutureDate) : undefined}
          aria-label={t('select-date')}
          tabIndex={-1}
        />
      </div>

      {canChangeForward ? (
        <button
          type="button"
          className="date-nav-arrow-btn"
          onClick={handleNextDay}
          aria-label="Next day"
        >
          <svg style={{ cursor: 'pointer' }} width="31" height="27">
            <use href={sprite + "#svg-arrow-right"} />
          </svg>
        </button>
      ) : (
        <span className="date-nav-arrow-disabled">
          <svg width="31" height="27" style={{ transform: 'rotate(180deg)' }}>
            <use href={sprite + "#svg-arrow-left"} />
          </svg>
        </span>
      )}

      {!isToday && (
        <button
          type="button"
          className="today-button selectable-button bold"
          onClick={handleToday}
          aria-label={t('today')}
        >
          {t('today')}
        </button>
      )}
    </span>
  );
};

export default DateSelector;
export { DateSelector };